// src/components/Auth/SignupScreen.jsx
import React, { useState, useEffect } from "react";
import { createUserWithEmailAndPassword } from "firebase/auth";
import { auth, db } from "../../firebase/config";
import { doc, setDoc } from "firebase/firestore";
import logo from "../../assets/nukkad-logo.png";
import "../../styles/SignupScreen.css";
import { validateInvite, markInviteUsed } from "../../utils/InviteManager";

export default function SignupScreen({ inviteCode: inviteCodeFromParams }) {
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [inviteCode, setInviteCode] = useState("");

  // Prefill invite code from URL
  useEffect(() => {
    if (inviteCodeFromParams) setInviteCode(inviteCodeFromParams);
  }, [inviteCodeFromParams]);

  // Capture the "Add to Home Screen" event globally
  useEffect(() => {
    window.deferredPrompt = null;

    window.addEventListener("beforeinstallprompt", (e) => {
      e.preventDefault();
      window.deferredPrompt = e; // Save the event
    });
  }, []);

  async function handleSignup(e) {
    e.preventDefault();
    setError("");
    setLoading(true);

    const name = e.target.name.value.trim();
    const email = e.target.email.value.trim();
    const phone = e.target.phone.value.trim();
    const password = e.target.password.value.trim();
    const invite = inviteCode.trim();

    try {
      // 1️⃣ Validate Invite
      const { valid } = await validateInvite(invite);
      if (!valid) {
        setError("❌ Invalid or already used invite code.");
        setLoading(false);
        return;
      }

      // 2️⃣ Create Auth Account
      const userCred = await createUserWithEmailAndPassword(auth, email, password);
      const uid = userCred.user.uid;

      // 3️⃣ Save User to Firestore
      await setDoc(doc(db, "users", uid), {
        name,
        email,
        phone,
        uid,
        createdAt: new Date(),
      });

      // 4️⃣ Mark Invite as Used
      await markInviteUsed(invite, { uid, name, email, phone });

      // ⭐⭐⭐ ADD TO HOME SCREEN — AUTO TRIGGER ⭐⭐⭐
      if (window.deferredPrompt) {
        const promptEvent = window.deferredPrompt;
        promptEvent.prompt(); // show popup
        window.deferredPrompt = null;
      }

      // 5️⃣ Redirect to login
      window.location.href = "/login";

    } catch (err) {
      console.error(err);
      setError(err.message || "Signup failed.");
    }

    setLoading(false);
  }

  return (
    <div className="signup-wrapper">
      <div className="signup-box">
        <img src={logo} alt="Nukkad Logo" className="signup-logo" />
        <h2 className="signup-title">Join Nukkad</h2>
        <p className="signup-sub">Secure Invite Verified ✓</p>

        <form onSubmit={handleSignup} className="signup-form">
          <label>Your Name</label>
          <input
            name="name"
            type="text"
            placeholder="Enter your name"
            required
          />

          <label>Email Address</label>
          <input
            name="email"
            type="email"
            placeholder="Enter your email"
            required
          />

          <label>Phone Number With Country Code</label>
          <input
            name="phone"
            type="tel"
            placeholder="+91xxxxxxxxxx"
            required
          />

          <label>Create Password</label>
          <input
            name="password"
            type="password"
            placeholder="Enter password"
            required
          />

          <label>Invite Code</label>
          <input
            name="invite"
            type="text"
            placeholder="Enter invite code"
            value={inviteCode}
            onChange={(e) => setInviteCode(e.target.value)}
            required
          />

          <button type="submit" disabled={loading}>
            {loading ? "Creating Account..." : "Create Account"}
          </button>
        </form>

        {error && <p className="signup-error">{error}</p>}

        <p className="signup-footer">
          Already have an account? <a href="/login">Login</a>
        </p>
      </div>
    </div>
  );
}
